import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, ArrowLeft, ArrowRight, Upload, CheckCircle, X, Search, RefreshCw } from 'lucide-react';
import { getBorrowers, getBorrowerById, createLoan, getSettingsByType } from '../services/api';
import { uploadMultipleImages } from '../services/cloudinary';
import Toast from '../components/Toast';
import ImageSourcePicker from '../components/ImageSourcePicker';
import { useLanguage } from '../contexts/LanguageContext';

const MIN_BORROWER_SEARCH_CHARS = 2;

/** Value for `<input type="date" />` (YYYY-MM-DD, local calendar day) */
function toDateInputValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return toDateInputValue(new Date());
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Selected calendar day + current local time → ISO string for API */
function selectedDateToCreateDateIso(yyyyMmDd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd);
  if (!m) return new Date().toISOString();
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const day = Number(m[3]);
  const now = new Date();
  return new Date(y, mo - 1, day, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()).toISOString();
}

function formatDateOnlyForUi(yyyyMmDdOrIso) {
  if (!yyyyMmDdOrIso) return '—';
  const localDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDdOrIso);
  if (localDay) {
    const y = Number(localDay[1]);
    const mo = Number(localDay[2]);
    const d = Number(localDay[3]);
    return new Date(y, mo - 1, d).toLocaleDateString();
  }
  const parsed = new Date(yyyyMmDdOrIso);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleDateString();
}

const CreateLoan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const texts = t('createLoan');
  const [currentStep, setCurrentStep] = useState(1);
  const [borrowerSearch, setBorrowerSearch] = useState('');
  const [borrowers, setBorrowers] = useState([]);
  const [borrowerSearchLoading, setBorrowerSearchLoading] = useState(false);
  const borrowerSearchTimeoutRef = useRef(null);
  const [selectedBorrower, setSelectedBorrower] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdLoanSummary, setCreatedLoanSummary] = useState(null);

  // Step 1: Borrower Selection
  const [isNewBorrowerMode, setIsNewBorrowerMode] = useState(false);

  // Step 2: Collateral Details
  const [collateralForm, setCollateralForm] = useState(() => ({
    metalType: 'Gold',
    itemDescription: '',
    purity: '24k',
    grossWeightGrams: '',
    netWeight: '',
    presentValueAmount: '',
    serialNumber: '',
    createDate: toDateInputValue(),
    collateralPhotos: []
  }));

  // Step 3: Loan Terms
  const [loanTerms, setLoanTerms] = useState({
    lendingRate: '6500',
    disbursedAmount: '',
    interestRate: '2.5',
    tenure: '12'
  });

  // Calculated maximum eligible amount
  const [maxEligibleAmount, setMaxEligibleAmount] = useState(0);

  // Loan settings state
  const [loanSettings, setLoanSettings] = useState([]);
  const [autoFilledFields, setAutoFilledFields] = useState({
    interestRate: false,
    ltvRate: false
  });

  // Today's market values state
  const [todayValues, setTodayValues] = useState({
    gold24k: 0,
    gold22k: 0,
    gold18k: 0,
    silver: 0
  });
  // Store raw values for display (10g for gold, 1kg for silver)
  const [todayValuesDisplay, setTodayValuesDisplay] = useState({
    gold24k: 0,
    gold22k: 0,
    gold18k: 0,
    silver: 0
  });
  const [marketValue, setMarketValue] = useState(0);

  const steps = [
    { id: 1, label: texts.steps?.borrower || 'Borrower', key: 'borrower' },
    { id: 2, label: texts.steps?.collateral || 'Collateral', key: 'collateral' },
    { id: 3, label: texts.steps?.terms || 'Terms', key: 'terms' },
    { id: 4, label: texts.steps?.confirm || 'Confirm', key: 'confirm' }
  ];

  // Load loan settings
  const loadLoanSettings = useCallback(async () => {
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        return;
      }

      const result = await getSettingsByType(businessId, 'LOAN');
      if (result.success && result.data) {
        setLoanSettings(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error('Error loading loan settings:', error);
    }
  }, []);

  // Load today's market values
  const loadTodayValues = useCallback(async () => {
    try {
      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        return;
      }

      const result = await getSettingsByType(businessId, 'TODAY_VALUE');
      if (result.success && result.data) {
        const rates = result.data;
        const gold24K = rates.find(rate => rate.name === '24K_GOLD');
        const silver = rates.find(rate => rate.name === 'SILVER');
        
        if (gold24K) {
          // Backend returns value for 10 grams of gold
          const gold24KPer10Grams = parseFloat(gold24K.amount);
          const gold24KPerGram = gold24KPer10Grams / 10; // Convert to per gram for calculations
          
          // Store per-gram values for calculations
          setTodayValues({
            gold24k: gold24KPerGram,
            gold22k: (gold24KPerGram * 22 / 24),
            gold18k: (gold24KPerGram * 18 / 24),
            silver: silver ? parseFloat(silver.amount) / 1000 : 0 // Backend returns per kg, convert to per gram
          });
          
          // Store display values (10g for gold, 1kg for silver) - as received from backend
          setTodayValuesDisplay({
            gold24k: gold24KPer10Grams,
            gold22k: (gold24KPer10Grams * 22 / 24),
            gold18k: (gold24KPer10Grams * 18 / 24),
            silver: silver ? parseFloat(silver.amount) : 0
          });
        } else if (silver) {
          // Backend returns value for 1 kg (1000 grams) of silver
          const silverPerKg = parseFloat(silver.amount);
          setTodayValues(prev => ({
            ...prev,
            silver: silverPerKg / 1000 // Per gram for calculations
          }));
          setTodayValuesDisplay(prev => ({
            ...prev,
            silver: silverPerKg // Display as 1kg value
          }));
        }
      }
    } catch (error) {
      console.error('Error loading today values:', error);
    }
  }, []);

  // Auto-fill Present Value Amount based on net weight and today's rates
  useEffect(() => {
    const netWeight = parseFloat(collateralForm.netWeight);
    
    if (!netWeight || netWeight <= 0 || !todayValues.gold24k) {
      return;
    }

    // Get today's rate based on metal type and purity
    let todayRate = 0;
    if (collateralForm.metalType === 'Gold') {
      if (collateralForm.purity === '24k') {
        todayRate = todayValues.gold24k;
      } else if (collateralForm.purity === '22k') {
        todayRate = todayValues.gold22k;
      } else if (collateralForm.purity === '18k') {
        todayRate = todayValues.gold18k;
      }
    } else if (collateralForm.metalType === 'Silver') {
      todayRate = todayValues.silver;
    }

    if (todayRate > 0) {
      // Auto-fill Present Value Amount = net weight × today's rate
      const calculatedPresentValue = (netWeight * todayRate).toFixed(2);
      setCollateralForm(prev => ({
        ...prev,
        presentValueAmount: calculatedPresentValue
      }));
    }
  }, [collateralForm.netWeight, collateralForm.metalType, collateralForm.purity, todayValues]);

  // Calculate market value and max eligible amount based on today's rates
  useEffect(() => {
    const netWeight = parseFloat(collateralForm.netWeight);
    
    if (!netWeight || netWeight <= 0) {
      setMarketValue(0);
      setMaxEligibleAmount(0);
      return;
    }

    // Get today's rate based on metal type and purity
    let todayRate = 0;
    if (collateralForm.metalType === 'Gold') {
      if (collateralForm.purity === '24k') {
        todayRate = todayValues.gold24k;
      } else if (collateralForm.purity === '22k') {
        todayRate = todayValues.gold22k;
      } else if (collateralForm.purity === '18k') {
        todayRate = todayValues.gold18k;
      }
    } else if (collateralForm.metalType === 'Silver') {
      todayRate = todayValues.silver;
    }

    // Calculate market value = net weight × today's rate
    const calculatedMarketValue = netWeight * todayRate;
    setMarketValue(calculatedMarketValue);

    // Auto-fill LTV rate with 75% of today's rate if user hasn't manually changed it
    if (todayRate > 0) {
      const suggestedLtvRate = (todayRate * 0.75).toFixed(0);
      const currentLtvRate = loanTerms.lendingRate;
      
      // Only auto-fill if: no value set OR default value OR not manually changed by user
      const shouldAutoFill = !currentLtvRate || 
                             currentLtvRate === '6500' || 
                             autoFilledFields.ltvRate !== false;
      
      if (shouldAutoFill) {
        setLoanTerms(prev => ({
          ...prev,
          lendingRate: suggestedLtvRate
        }));
        setAutoFilledFields(prev => ({ ...prev, ltvRate: true }));
      }
    }

    // Calculate max eligible based on LTV rate (per gram)
    const ltvRate = parseFloat(loanTerms.lendingRate);
    if (ltvRate && ltvRate > 0) {
      setMaxEligibleAmount(netWeight * ltvRate);
    } else {
      // If no LTV rate set, default to 75% of market value
      setMaxEligibleAmount(calculatedMarketValue * 0.75);
    }
  }, [collateralForm.netWeight, collateralForm.metalType, collateralForm.purity, todayValues, autoFilledFields.ltvRate, loanTerms.lendingRate]);

  // Auto-fill interest rate based on Metal Type and disbursed amount range
  useEffect(() => {
    // Only auto-fill if we have Metal Type, disbursed amount, and loan settings
    if (!collateralForm.metalType || !loanTerms.disbursedAmount || loanSettings.length === 0) {
      setAutoFilledFields({ interestRate: false });
      return;
    }

    // Map Metal Type to loan setting name (Gold or Silver)
    const metalTypeName = collateralForm.metalType; // 'Gold' or 'Silver'

    // Find matching loan settings by metal type name
    const matchingSettings = loanSettings.filter(setting => setting.name === metalTypeName);
    
    if (matchingSettings.length === 0) {
      setAutoFilledFields({ interestRate: false });
      return;
    }

    const disbursedAmount = parseFloat(loanTerms.disbursedAmount);
    if (!disbursedAmount || disbursedAmount <= 0) {
      setAutoFilledFields({ interestRate: false });
      return;
    }

    // Find the best matching setting based on amount range
    let bestMatch = null;

    for (const setting of matchingSettings) {
      // Check if amount range exists and matches
      if (setting.amount) {
        const amountRange = setting.amount;
        if (amountRange.includes(' - ')) {
          // Range format: "1000 - 10000"
          const [minStr, maxStr] = amountRange.split(' - ');
          const min = parseFloat(minStr.trim());
          const max = parseFloat(maxStr.trim());

          if (!isNaN(min) && !isNaN(max) && disbursedAmount >= min && disbursedAmount <= max) {
            bestMatch = setting;
            break;
          }
        } else {
          // Single value format: "20000.00"
          const singleAmount = parseFloat(amountRange);
          if (!isNaN(singleAmount)) {
            // Check if disbursed amount is within ±10% of the single amount
            const tolerance = singleAmount * 0.1;
            if (Math.abs(disbursedAmount - singleAmount) <= tolerance) {
              bestMatch = setting;
              break;
            }
          }
        }
      } else {
        // No amount range specified, use this setting
        if (!bestMatch) {
          bestMatch = setting;
        }
      }
    }

    // If no match found with range, use the first matching setting for the metal type
    if (!bestMatch && matchingSettings.length > 0) {
      bestMatch = matchingSettings[0];
    }

    // Auto-fill only the interest rate
    // Auto-fill if there's a match and user hasn't manually changed it
    if (bestMatch) {
      // Only skip auto-fill if user has explicitly manually changed the interest rate
      // autoFilledFields.interestRate === false means user manually changed it
      // We'll only prevent auto-fill if the flag is false AND the value is different from what we'd set
      const currentInterestRate = parseFloat(loanTerms.interestRate);
      const newInterestRate = bestMatch.interestRate;
      
      // Don't auto-fill if: user manually changed it (flag is false) AND current value is different from new value
      const shouldSkipAutoFill = autoFilledFields.interestRate === false && 
                                 currentInterestRate !== newInterestRate &&
                                 currentInterestRate !== 2.5; // Not the default value
      
      if (!shouldSkipAutoFill) {
        setLoanTerms(prev => ({
          ...prev,
          interestRate: bestMatch.interestRate.toString()
        }));
        
        // Mark interest rate as auto-filled
        setAutoFilledFields({
          interestRate: true
        });
      }
    } else {
      // No match found - clear auto-fill flag if it was previously auto-filled
      if (autoFilledFields.interestRate) {
        setAutoFilledFields({
          interestRate: false
        });
      }
    }
  }, [collateralForm.metalType, loanTerms.disbursedAmount, loanTerms.interestRate, loanSettings, autoFilledFields.interestRate]);

  // Handle loan terms change and clear auto-fill flag when user manually changes fields
  const handleLoanTermsChange = (e) => {
    const { name, value } = e.target;
    const previousValue = loanTerms[name];
    
    setLoanTerms(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If user manually changes interest rate (and it was previously auto-filled), mark as manually changed
    if (name === 'interestRate' && autoFilledFields.interestRate && value !== previousValue) {
      setAutoFilledFields(prev => ({ ...prev, interestRate: false }));
    }
    
    // If user manually changes LTV rate, mark as manually changed
    if (name === 'lendingRate' && value !== previousValue) {
      setAutoFilledFields(prev => ({ ...prev, ltvRate: false }));
    }
  };

  const searchBorrowers = useCallback(async (term) => {
    const query = (term || '').trim();
    if (query.length < MIN_BORROWER_SEARCH_CHARS) {
      setBorrowers([]);
      return;
    }

    setBorrowerSearchLoading(true);
    try {
      const result = await getBorrowers({
        page: 0,
        size: 20,
        sortBy: 'createdAt',
        sortDir: 'desc',
        search: query
      });
      if (result.success && result.data) {
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data.content)
            ? result.data.content
            : [];
        setBorrowers(list);
      } else {
        setBorrowers([]);
      }
    } catch (error) {
      console.error('Error searching borrowers:', error);
      setBorrowers([]);
    } finally {
      setBorrowerSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLoanSettings();
    loadTodayValues();
  }, [loadLoanSettings, loadTodayValues]);

  useEffect(() => {
    if (borrowerSearchTimeoutRef.current) {
      clearTimeout(borrowerSearchTimeoutRef.current);
    }

    const query = borrowerSearch.trim();
    if (query.length < MIN_BORROWER_SEARCH_CHARS) {
      setBorrowers([]);
      setBorrowerSearchLoading(false);
      return undefined;
    }

    borrowerSearchTimeoutRef.current = setTimeout(() => {
      searchBorrowers(query);
    }, 400);

    return () => {
      if (borrowerSearchTimeoutRef.current) {
        clearTimeout(borrowerSearchTimeoutRef.current);
      }
    };
  }, [borrowerSearch, searchBorrowers]);

  // Handle borrower selection from redirect (after creating new borrower)
  useEffect(() => {
    const selectedBorrowerId = location.state?.selectedBorrowerId;
    if (!selectedBorrowerId) return;

    let isMounted = true;

    (async () => {
      try {
        const response = await getBorrowerById(selectedBorrowerId);
        if (!isMounted) return;
        if (response.success && response.data) {
          setSelectedBorrower(response.data);
          setCurrentStep(2);
          window.history.replaceState({}, document.title);
          setToast({
            show: true,
            message: `Borrower "${response.data.fullName}" selected. Now add collateral details.`,
            type: 'success'
          });
        }
      } catch (error) {
        console.error('Error loading selected borrower:', error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [location.state]);

  const clearBorrowerSearch = () => {
    setBorrowerSearch('');
    setBorrowers([]);
  };

  const handleNext = () => {
    // Validation before proceeding
    if (currentStep === 1 && !selectedBorrower && !isNewBorrowerMode) {
      setToast({ show: true, message: texts.messages?.selectBorrower || 'Please select a borrower or create a new one', type: 'error' });
      return;
    }
    if (currentStep === 2) {
      if (!collateralForm.metalType || !collateralForm.itemDescription || !collateralForm.netWeight || !collateralForm.grossWeightGrams || !collateralForm.presentValueAmount) {
        setToast({ show: true, message: texts.messages?.fillCollateralDetails || 'Please fill in all required collateral details', type: 'error' });
        return;
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(collateralForm.createDate || '')) {
        setToast({ show: true, message: texts.messages?.selectCreatingDate || 'Please select a valid creating date', type: 'error' });
        return;
      }
    }
    if (currentStep === 3) {
      if (!loanTerms.disbursedAmount || !loanTerms.interestRate || !loanTerms.tenure) {
        setToast({ show: true, message: texts.messages?.fillLoanTerms || 'Please fill in all loan terms', type: 'error' });
        return;
      }
      if (parseFloat(loanTerms.disbursedAmount) > maxEligibleAmount) {
        setToast({ show: true, message: texts.messages?.disbursedAmountExceeds || 'Disbursed amount cannot exceed maximum eligible amount', type: 'error' });
        return;
      }
    }
    
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCollateralFormChange = (e) => {
    const { name, value } = e.target;
    
    // If metal type changes, reset purity to appropriate default
    if (name === 'metalType') {
      setCollateralForm(prev => ({
        ...prev,
        [name]: value,
        purity: value === 'Gold' ? '24k' : 'Pure'
      }));
    } else {
      setCollateralForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };


  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setCollateralForm(prev => ({
        ...prev,
        collateralPhotos: [...prev.collateralPhotos, ...files]
      }));
    }
  };

  const removePhoto = (index) => {
    setCollateralForm(prev => ({
      ...prev,
      collateralPhotos: prev.collateralPhotos.filter((_, i) => i !== index)
    }));
  };

  // Helper function to get today's rate for selected metal and purity
  const getTodayRateForSelected = () => {
    if (collateralForm.metalType === 'Gold') {
      if (collateralForm.purity === '24k') return todayValues.gold24k;
      if (collateralForm.purity === '22k') return todayValues.gold22k;
      if (collateralForm.purity === '18k') return todayValues.gold18k;
    } else if (collateralForm.metalType === 'Silver') {
      return todayValues.silver;
    }
    return 0;
  };

  const handleSubmitLoan = async () => {
    setIsSubmitting(true);
    try {
      // 1) Upload photos to Cloudinary (if any)
      let uploadedPhotoUrls = [];
      if (collateralForm.collateralPhotos && collateralForm.collateralPhotos.length > 0) {
        try {
          uploadedPhotoUrls = await uploadMultipleImages(collateralForm.collateralPhotos, { folder: 'loan-collateral' });
        } catch (uploadErr) {
          console.error('Cloudinary upload error:', uploadErr);
          setToast({ show: true, message: texts.messages?.uploadFailed || 'Failed to upload photos. Please try again.', type: 'error' });
          setIsSubmitting(false);
          return;
        }
      }

      // Map silver purity values to backend-acceptable karat values
      let purityKaratValue = collateralForm.purity;
      if (collateralForm.metalType === 'Silver') {
        if (collateralForm.purity === 'Pure') {
          purityKaratValue = '24k'; // Pure silver mapped to 24k
        } else if (collateralForm.purity === 'Ornament') {
          purityKaratValue = '22k'; // Ornament silver mapped to 22k
        }
      }

      // Prepare loan data with correct API field names
      const loanData = {
        name: selectedBorrower.fullName, // Borrower name as loan name
        borrowerId: selectedBorrower.id,
        metalType: collateralForm.metalType.toUpperCase(), // GOLD or SILVER
        itemDescription: collateralForm.itemDescription,
        purityKarat: purityKaratValue, // 24k, 22k, 18k (mapped from Pure/Ornament for silver)
        grossWeightGrams: parseFloat(collateralForm.grossWeightGrams),
        netWeightGrams: parseFloat(collateralForm.netWeight),
        presentValueAmount: parseFloat(collateralForm.presentValueAmount),
        photosUrl: uploadedPhotoUrls[0] || 'string',
        lendingRatePerGram: parseFloat(loanTerms.lendingRate),
        finalDisbursedAmount: parseFloat(loanTerms.disbursedAmount),
        interestRatePerMonth: parseFloat(loanTerms.interestRate),
        tenureMonths: parseInt(loanTerms.tenure),
        status: 'ACTIVE', // Default status - created loans are active by default (not shown on UI)
        collateralStatus: 'IN_HAND', // Automatically set to IN_HAND when loan is created
        createdAt: selectedDateToCreateDateIso(collateralForm.createDate),
        ...(collateralForm.serialNumber ? { serialNumber: collateralForm.serialNumber } : {})
      };

      console.log('📤 Creating loan with data:', loanData);

      const result = await createLoan(loanData);
      
      if (result.success) {
        setToast({ show: true, message: texts.messages?.loanCreatedSuccess || `Loan created successfully for ${selectedBorrower.fullName}!`, type: 'success' });
        // Keep user on the same page and show bill preview
        const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
        const billData = {
          businessName: selectedBusiness?.businessName || 'Your Shop Name',
          businessContact: selectedBusiness?.contactNumber || '',
          businessAddress: selectedBusiness?.address || '',
          borrower: selectedBorrower,
          loan: {
            ...loanData,
            purityKarat: collateralForm.purity, // Use original user-friendly name for display
            id: result.data?.id || undefined
          },
          createdAt: loanData.createdAt || new Date().toISOString()
        };
        setCreatedLoanSummary(billData);
      } else {
        setToast({ show: true, message: result.message || 'Failed to create loan. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('❌ Loan creation error:', error);
      setToast({ show: true, message: 'Failed to create loan. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetCreateLoan = () => {
    setCreatedLoanSummary(null);
    setCurrentStep(1);
    setSelectedBorrower(null);
    setIsNewBorrowerMode(false);
    setCollateralForm({
      metalType: 'Gold',
      itemDescription: '',
      purity: '24k',
      grossWeightGrams: '',
      netWeight: '',
      presentValueAmount: '',
      serialNumber: '',
      createDate: toDateInputValue(),
      collateralPhotos: []
    });
    setLoanTerms({
      lendingRate: '6500',
      disbursedAmount: '',
      interestRate: '2.5',
      tenure: '12'
    });
    setAutoFilledFields({
      interestRate: false,
      ltvRate: false
    });
  };

  const printBill = () => {
    if (!createdLoanSummary) return;
    const billHtml = getBillHtml(createdLoanSummary);
    const win = window.open('', '_blank', 'width=800,height=900');
    if (!win) return;
    win.document.open();
    win.document.write(billHtml);
    win.document.close();
    win.focus();
    // Give time for images/fonts
    setTimeout(() => {
      win.print();
    }, 500);
  };

  const getBillHtml = (data) => {
    const loan = data.loan;
    const borrower = data.borrower;
    const businessName = data.businessName;
    const businessContact = data.businessContact;
    const businessAddress = data.businessAddress;
    const totalInterest = (loan.finalDisbursedAmount * loan.interestRatePerMonth * loan.tenureMonths) / 100;
    const totalRepay = loan.finalDisbursedAmount + totalInterest;
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Loan Bill</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 24px; color: #111827; }
    .bill { max-width: 800px; margin: 0 auto; border: 1px solid #e5e7eb; border-radius: 8px; padding: 24px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
    .shop { font-size: 20px; font-weight: 700; color: #1f2937; }
    .muted { color: #6b7280; font-size: 12px; }
    .section { margin-top: 16px; }
    .title { font-size: 14px; font-weight: 700; margin-bottom: 8px; color: #374151; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 16px; }
    .row { display: flex; justify-content: space-between; margin: 4px 0; }
    .label { color: #6b7280; }
    .value { font-weight: 600; }
    .total { display: flex; justify-content: space-between; border-top: 2px solid #e5e7eb; padding-top: 8px; margin-top: 8px; font-size: 16px; font-weight: 700; color: #065f46; }
    .footer { margin-top: 24px; text-align: center; font-size: 12px; color: #6b7280; }
    img.preview { max-width: 200px; max-height: 200px; object-fit: cover; border: 1px solid #e5e7eb; border-radius: 6px; }
    @media print { body { padding: 0; } .bill { border: none; } }
  </style>
  </head>
  <body>
    <div class="bill">
      <div class="header">
        <div>
          <div class="shop">${businessName}</div>
          ${businessAddress ? `<div class="muted">${businessAddress}</div>` : ''}
          ${businessContact ? `<div class="muted">Contact: ${businessContact}</div>` : ''}
        </div>
        <div class="muted" style="text-align:right;">
          <div><strong>Bill Date:</strong> ${new Date(data.createdAt).toLocaleString()}</div>
          ${loan.id ? `<div><strong>Loan ID:</strong> ${loan.id}</div>` : ''}
        </div>
      </div>

      <div class="section">
        <div class="title">Borrower</div>
        <div class="grid">
          <div><span class="label">Name</span><div class="value">${borrower.fullName}</div></div>
          <div><span class="label">Phone</span><div class="value">${borrower.contactNumber || '-'}</div></div>
          ${borrower.email ? `<div><span class="label">Email</span><div class="value">${borrower.email}</div></div>` : ''}
          ${borrower.address ? `<div><span class="label">Address</span><div class="value">${borrower.address}</div></div>` : ''}
        </div>
      </div>

      <div class="section">
        <div class="title">Collateral</div>
        <div class="grid">
          <div><span class="label">Metal Type</span><div class="value">${loan.metalType}</div></div>
          <div><span class="label">Purity</span><div class="value">${loan.purityKarat}</div></div>
          <div><span class="label">Gross Weight (g)</span><div class="value">${loan.grossWeightGrams || '-'}</div></div>
          <div><span class="label">Net Weight (g)</span><div class="value">${loan.netWeightGrams}</div></div>
          <div><span class="label">Present Value</span><div class="value">₹ ${loan.presentValueAmount ? parseFloat(loan.presentValueAmount).toLocaleString('en-IN') : '-'}</div></div>
          <div><span class="label">LTV Rate (per g)</span><div class="value">₹ ${loan.lendingRatePerGram}</div></div>
        </div>
        ${loan.itemDescription ? `<div class="row"><span class="label">Item Description</span><span class="value">${loan.itemDescription}</span></div>` : ''}
        ${loan.createdAt ? `<div class="row"><span class="label">Creating date</span><span class="value">${new Date(loan.createdAt).toLocaleDateString()}</span></div>` : ''}
      </div>

      <div class="section">
        <div class="title">Loan Terms</div>
        <div class="row"><span class="label">Disbursed Amount</span><span class="value">₹ ${loan.finalDisbursedAmount.toLocaleString('en-IN')}</span></div>
        <div class="row"><span class="label">Interest Rate (per month)</span><span class="value">${loan.interestRatePerMonth}%</span></div>
        <div class="row"><span class="label">Tenure</span><span class="value">${loan.tenureMonths} months</span></div>
        <div class="row"><span class="label">Status</span><span class="value">${loan.status}</span></div>
        <div class="row"><span class="label">Total Interest</span><span class="value">₹ ${totalInterest.toLocaleString('en-IN')}</span></div>
        <div class="total"><span>Total Repayable</span><span>₹ ${totalRepay.toLocaleString('en-IN')}</span></div>
      </div>

      <div class="footer">Thank you for choosing ${businessName}. Please keep this bill for your records.</div>
    </div>
  </body>
</html>`;
  };

  const borrowerSearchTrimmed = borrowerSearch.trim();
  const borrowerSearchTooShort = borrowerSearchTrimmed.length > 0 && borrowerSearchTrimmed.length < MIN_BORROWER_SEARCH_CHARS;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
              {texts.step1?.title || 'Step 1: Select or Create Borrower'}
            </h3>
            
            <div style={{ marginBottom: '2rem' }}>
              <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                {texts.step1?.searchExistingBorrower || 'Search Existing Borrower'}
              </h4>
              <div className="search-field-wrap">
                <div className="search-field">
                  <Search
                    size={18}
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-secondary)',
                      pointerEvents: 'none'
                    }}
                  />
                  <input
                    type="text"
                    className={`form-input${borrowerSearchTooShort ? ' search-input--error' : ''}`}
                    placeholder={texts.step1?.searchPlaceholder || "Start typing name or phone number..."}
                    value={borrowerSearch}
                    onChange={(e) => setBorrowerSearch(e.target.value)}
                    style={{ paddingLeft: '2.5rem' }}
                  />
                  {borrowerSearch && (
                    <button
                      type="button"
                      className="search-field-clear"
                      onClick={clearBorrowerSearch}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {borrowerSearchTooShort && (
                  <p className="list-filter-search-error">
                    {t('loans.messages.searchMinChars') || 'Enter at least 2 characters to search'}
                  </p>
                )}
              </div>
            </div>

            {/* Borrower List */}
            {borrowerSearchTrimmed.length >= MIN_BORROWER_SEARCH_CHARS && (
              <div style={{ marginBottom: '2rem' }}>
                <div className="search-result-list">
                  {borrowerSearchLoading ? (
                    <div className="search-result-empty" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      {texts.step1?.searching || 'Searching borrowers...'}
                    </div>
                  ) : borrowers.length === 0 ? (
                    <div className="search-result-empty">
                      {texts.step1?.noBorrowersFound || 'No borrowers found'}
                    </div>
                  ) : (
                    borrowers.map((borrower) => (
                      <div
                        key={borrower.id}
                        onClick={() => setSelectedBorrower(borrower)}
                        className={`search-result-item${selectedBorrower?.id === borrower.id ? ' is-selected' : ''}`}
                      >
                        <div className="search-result-title">{borrower.fullName}</div>
                        <div className="search-result-meta">{borrower.contactNumber}</div>
                        {borrower.email && (
                          <div className="search-result-meta">{borrower.email}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Selected Borrower Display */}
            {selectedBorrower && (
              <div className="selection-success-card" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <CheckCircle size={20} color="#10b981" />
                  <span style={{ fontWeight: '600', color: '#34d399' }}>{texts.step1?.selectedBorrower || 'Selected Borrower'}</span>
                </div>
                <div style={{ color: 'var(--text-primary)' }}><strong>{texts.step1?.name || 'Name'}:</strong> {selectedBorrower.fullName}</div>
                <div style={{ color: 'var(--text-primary)' }}><strong>{texts.step1?.phone || 'Phone'}:</strong> {selectedBorrower.contactNumber}</div>
                {selectedBorrower.email && (
                  <div style={{ color: 'var(--text-primary)' }}><strong>{texts.step1?.email || 'Email'}:</strong> {selectedBorrower.email}</div>
                )}
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                {texts.step1?.orNewCustomer || 'Or, if this is a new customer:'}
              </p>
              <Link 
                to="/borrowers/create" 
                state={{ fromLoan: true }}
                className="btn btn-secondary"
              >
                <Plus size={16} />
                {texts.step1?.createNewBorrower || 'Create New Borrower Profile'}
              </Link>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
              {texts.step2?.title || 'Step 2: Collateral Details'}
            </h3>
            
            {/* Show selected borrower info */}
            {selectedBorrower && (
              <div className="info-banner-success" style={{ marginBottom: '1.5rem', borderRadius: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <CheckCircle size={18} color="#10b981" />
                  <span className="info-banner-success-title">{texts.step2?.loanFor || 'Loan for'}: {selectedBorrower.fullName}</span>
                </div>
              </div>
            )}

            {/* Display Today's Market Rates */}
            {todayValuesDisplay.gold24k > 0 && (
              <div className="info-banner-warning" style={{ marginBottom: '1.5rem' }}>
                <div className="info-banner-warning-title">
                  {texts.step2?.todayMarketRates || '📊 Today\'s Market Rates'}
                </div>
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                  <div>
                    <span className="info-banner-warning-label">{texts.step2?.gold24k || '24K Gold (10g)'}:</span>
                    <strong style={{ marginLeft: '0.25rem', color: 'var(--text-primary)' }}>₹{todayValuesDisplay.gold24k.toLocaleString('en-IN')}</strong>
                  </div>
                  <div>
                    <span className="info-banner-warning-label">{texts.step2?.gold22k || '22K Gold (10g)'}:</span>
                    <strong style={{ marginLeft: '0.25rem', color: 'var(--text-primary)' }}>₹{todayValuesDisplay.gold22k.toFixed(2).toLocaleString('en-IN')}</strong>
                  </div>
                  <div>
                    <span className="info-banner-warning-label">{texts.step2?.gold18k || '18K Gold (10g)'}:</span>
                    <strong style={{ marginLeft: '0.25rem', color: 'var(--text-primary)' }}>₹{todayValuesDisplay.gold18k.toFixed(2).toLocaleString('en-IN')}</strong>
                  </div>
                  {todayValuesDisplay.silver > 0 && (
                    <div>
                      <span className="info-banner-warning-label">{texts.step2?.silver || 'Silver (1kg)'}:</span>
                      <strong style={{ marginLeft: '0.25rem', color: 'var(--text-primary)' }}>₹{todayValuesDisplay.silver.toLocaleString('en-IN')}</strong>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* First Row - 4 columns */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label">{texts.step2?.serialNumber || 'Serial Number (Optional)'}</label>
                <input
                  type="text"
                  name="serialNumber"
                  className="form-input"
                  placeholder={texts.step2?.serialNumberPlaceholder || "e.g., SN123456"}
                  value={collateralForm.serialNumber}
                  onChange={handleCollateralFormChange}
                />
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step2?.metalType || 'Metal Type'} *</label>
                <select
                  name="metalType"
                  className="form-select"
                  value={collateralForm.metalType}
                  onChange={handleCollateralFormChange}
                  required
                >
                  <option value="Gold">{texts.step2?.gold || 'Gold'}</option>
                  <option value="Silver">{texts.step2?.silverOption || 'Silver'}</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step2?.purity || 'Purity/Karat'} *</label>
                <select
                  name="purity"
                  className="form-select"
                  value={collateralForm.purity}
                  onChange={handleCollateralFormChange}
                  required
                >
                  {collateralForm.metalType === 'Gold' ? (
                    <>
                      <option value="24k">{texts.step2?.purity24k || '24k (24 Karat)'}</option>
                      <option value="22k">{texts.step2?.purity22k || '22k (22 Karat)'}</option>
                      <option value="18k">{texts.step2?.purity18k || '18k (18 Karat)'}</option>
                    </>
                  ) : (
                    <>
                      <option value="Pure">{texts.step2?.purityPure || 'Pure Silver'}</option>
                      <option value="Ornament">{texts.step2?.purityOrnament || 'Ornament'}</option>
                    </>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step2?.creatingDate || 'Creating date'} *</label>
                <input
                  type="date"
                  name="createDate"
                  className="form-input"
                  value={collateralForm.createDate}
                  onChange={handleCollateralFormChange}
                  required
                />
              </div>
            </div>

            {/* Second Row - 3 columns */}
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
              <div className="form-group">
                <label className="form-label">{texts.step2?.grossWeight || 'Gross Weight (in grams)'} *</label>
                <input
                  type="number"
                  name="grossWeightGrams"
                  className="form-input"
                  placeholder={texts.step2?.grossWeightPlaceholder || "e.g., 55.5"}
                  step="0.001"
                  min="0.001"
                  value={collateralForm.grossWeightGrams}
                  onChange={handleCollateralFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step2?.netWeight || 'Net Weight (in grams)'} *</label>
                <input
                  type="number"
                  name="netWeight"
                  className="form-input"
                  placeholder={texts.step2?.netWeightPlaceholder || "e.g., 50.5"}
                  step="0.001"
                  min="0.001"
                  value={collateralForm.netWeight}
                  onChange={handleCollateralFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step2?.presentValue || 'Present Value Amount (₹)'} *</label>
                <input
                  type="number"
                  name="presentValueAmount"
                  className="form-input"
                  placeholder={texts.step2?.presentValuePlaceholder || "e.g., 325000"}
                  step="0.01"
                  min="0.01"
                  value={collateralForm.presentValueAmount}
                  onChange={handleCollateralFormChange}
                  required
                />
                {collateralForm.presentValueAmount && getTodayRateForSelected() > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem', fontWeight: '500' }}>
                    ✓ {texts.step2?.autoCalculated || 'Auto-calculated using today\'s market rate'}
                  </div>
                )}
              </div>
            </div>

            {/* Third Row - 2 columns */}
            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label className="form-label">{texts.step2?.itemDescription || 'Item Description'} *</label>
                <textarea
                  name="itemDescription"
                  className="form-textarea"
                  placeholder={texts.step2?.itemDescriptionPlaceholder || "Describe the collateral (e.g., Gold Chain, Silver Bangle set)"}
                  value={collateralForm.itemDescription}
                  onChange={handleCollateralFormChange}
                  rows={5}
                  style={{ minHeight: '130px', maxHeight: '130px' }}
                  required
                />
              </div>

              {/* Photo Upload */}
              <div className="form-group">
                <label className="form-label">{texts.step2?.collateralPhotos || 'Collateral Photos'}</label>
                <div
                  style={{
                    border: '2px dashed var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1.5rem',
                    textAlign: 'center',
                    backgroundColor: collateralForm.collateralPhotos.length > 0 ? 'rgba(59, 130, 246, 0.12)' : 'transparent'
                  }}
                >
                  <Upload size={24} style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem' }} />
                  {collateralForm.collateralPhotos.length > 0 ? (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ color: '#34d399', marginBottom: '0.25rem', fontWeight: '500' }}>
                        {collateralForm.collateralPhotos.length} {texts.step2?.photos || 'photo(s) selected'}
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                        {texts.step2?.clickToUpload || 'Add more from gallery or camera'}
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
                      {texts.step2?.clickToUpload || 'Upload photos'}
                    </div>
                  )}
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
                    {texts.step2?.supportedFormats || 'Supports: JPEG, PNG (Max 5MB each)'}
                  </div>
                  <ImageSourcePicker
                    multiple
                    onChange={handlePhotoUpload}
                    galleryLabel={t('common.gallery')}
                    cameraLabel={t('common.camera')}
                  />
                </div>

                {/* Display uploaded photos */}
                {collateralForm.collateralPhotos.length > 0 && (
                  <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem' }}>
                    {collateralForm.collateralPhotos.map((photo, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img
                          src={URL.createObjectURL(photo)}
                          alt={`Collateral ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '0.5rem',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
              {texts.step3?.title || 'Step 3: Loan Terms'}
            </h3>
            
            {/* Market Value and LTV Information */}
            {marketValue > 0 && (
              <div className="create-loan-terms-summary">
                <div className="form-grid create-loan-terms-summary-grid">
                  <div>
                    <div className="create-loan-terms-summary-label">
                      {texts.step3?.marketValue || 'Market Value'} ({collateralForm.metalType} {collateralForm.purity})
                    </div>
                    <div className="create-loan-terms-summary-value is-green">
                      ₹{marketValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="create-loan-terms-summary-weight">
                      {collateralForm.netWeight}g × ₹{getTodayRateForSelected().toLocaleString('en-IN')}/g
                    </div>
                  </div>
                  <div>
                    <div className="create-loan-terms-summary-label">
                      {texts.step3?.maxEligibleAmount || 'Maximum Eligible Amount'}
                    </div>
                    <div className="create-loan-terms-summary-value is-blue">
                      ₹{maxEligibleAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                    <div className="create-loan-terms-summary-hint">
                      Based on LTV Rate
                    </div>
                  </div>
                  <div>
                    <div className="create-loan-terms-summary-label">
                      LTV Percentage
                    </div>
                    <div className="create-loan-terms-summary-value is-purple">
                      {loanTerms.disbursedAmount && marketValue > 0 
                        ? ((parseFloat(loanTerms.disbursedAmount) / marketValue) * 100).toFixed(1)
                        : '0.0'}%
                    </div>
                    <div className="create-loan-terms-summary-hint">
                      Disbursed / Market Value
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
              <div className="form-group">
                <label className="form-label">{texts.step3?.ltvRate || 'LTV Rate (per gram)'} *</label>
                <input
                  type="number"
                  name="lendingRate"
                  className="form-input"
                  placeholder={texts.step3?.ltvRatePlaceholder || "e.g., 6500"}
                  value={loanTerms.lendingRate}
                  onChange={handleLoanTermsChange}
                  required
                />
                {getTodayRateForSelected() > 0 && (
                  <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem', fontWeight: '500' }}>
                    {texts.step3?.autoFilledLTV || '💡 Auto-filled'} ₹{(getTodayRateForSelected() * 0.75).toFixed(0)}/g (75% LTV)
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step3?.finalDisbursedAmount || 'Final Disbursed Amount (₹)'} *</label>
                <input
                  type="number"
                  name="disbursedAmount"
                  className="form-input"
                  placeholder={texts.step3?.disbursedAmountPlaceholder || "e.g., 325000"}
                  step="0.01"
                  value={loanTerms.disbursedAmount}
                  onChange={handleLoanTermsChange}
                  required
                />
                {parseFloat(loanTerms.disbursedAmount) > maxEligibleAmount && (
                  <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    ⚠️ Amount exceeds maximum eligible
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step3?.interestRate || 'Interest Rate (% per month)'} *</label>
                <input
                  type="number"
                  name="interestRate"
                  className="form-input"
                  placeholder={texts.step3?.interestRatePlaceholder || "e.g., 2.5"}
                  step="0.1"
                  value={loanTerms.interestRate}
                  onChange={handleLoanTermsChange}
                  required
                />
                {autoFilledFields.interestRate && (
                  <div style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem' }}>
                    ✓ Auto-filled from loan settings
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{texts.step3?.tenure || 'Tenure (in months)'} *</label>
                <input
                  type="number"
                  name="tenure"
                  className="form-input"
                  placeholder={texts.step3?.tenurePlaceholder || "e.g., 12"}
                  value={loanTerms.tenure}
                  onChange={handleLoanTermsChange}
                  required
                />
              </div>
            </div>

            {/* Interest Calculation Preview */}
            {loanTerms.disbursedAmount && loanTerms.interestRate && loanTerms.tenure && (
              <div className="calc-preview-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span className="calc-preview-label">Principal Amount:</span>
                  <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
                    ₹{parseFloat(loanTerms.disbursedAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="calc-preview-label">Monthly Interest:</span>
                  <span className="calc-preview-value" style={{ fontSize: '1.125rem' }}>
                    ₹{((parseFloat(loanTerms.disbursedAmount) * parseFloat(loanTerms.interestRate)) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>
              {texts.step4?.title || 'Step 4: Confirmation'}
            </h3>
            
            <div className="info-banner-success" style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <CheckCircle size={20} color="#10b981" />
                <span className="info-banner-success-title">{texts.step4?.reviewLoanDetails || 'Review Loan Details'}</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: 0 }}>
                {texts.step4?.reviewMessage || 'Please review all the details before confirming the loan. Once confirmed, the loan will be created in the system.'}
              </p>
            </div>

            {/* Borrower Details Summary */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 className="confirm-section-title">
                {texts.step4?.borrowerDetails || 'Borrower Details'}
              </h4>
              {selectedBorrower ? (
                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="confirm-field">
                    <strong>{texts.step4?.name || 'Full Name'}:</strong> {selectedBorrower.fullName}
                  </div>
                  <div className="confirm-field">
                    <strong>{texts.step4?.phone || 'Phone Number'}:</strong> {selectedBorrower.contactNumber}
                  </div>
                  {selectedBorrower.email && (
                    <div className="confirm-field">
                      <strong>{texts.step4?.email || 'Email'}:</strong> {selectedBorrower.email}
                    </div>
                  )}
                  {selectedBorrower.address && (
                    <div className="confirm-field">
                      <strong>{texts.step4?.address || 'Address'}:</strong> {selectedBorrower.address}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ color: '#ef4444' }}>
                  {texts.step4?.noBorrowerSelected || '⚠️ No borrower selected. Please go back and select a borrower.'}
                </div>
              )}
            </div>

            {/* Collateral Details Summary */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 className="confirm-section-title">
                {texts.step4?.collateralDetails || 'Collateral Details'}
              </h4>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="confirm-field">
                  <strong>{texts.step4?.metalType || 'Metal Type'}:</strong> {collateralForm.metalType}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.purityKarat || 'Purity/Karat'}:</strong> {collateralForm.purity === 'Pure' ? 'Pure Silver' : collateralForm.purity === 'Ornament' ? 'Ornament' : collateralForm.purity}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.grossWeight || 'Gross Weight'}:</strong> {collateralForm.grossWeightGrams} {texts.units?.grams || 'grams'}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.netWeight || 'Net Weight'}:</strong> {collateralForm.netWeight} {texts.units?.grams || 'grams'}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.presentValueAmount || 'Present Value Amount'}:</strong> ₹{parseFloat(collateralForm.presentValueAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                </div>
                {collateralForm.serialNumber && (
                  <div className="confirm-field">
                    <strong>{texts.step4?.serialNumber || 'Serial Number'}:</strong> {collateralForm.serialNumber}
                  </div>
                )}
                <div className="confirm-field">
                  <strong>{texts.step4?.creatingDate || 'Creating date'}:</strong>{' '}
                  {formatDateOnlyForUi(collateralForm.createDate)}
                </div>
                {collateralForm.collateralPhotos.length > 0 && (
                  <div className="confirm-field">
                    <strong>{texts.step4?.photos || 'Photos'}:</strong> {collateralForm.collateralPhotos.length} uploaded
                  </div>
                )}
              </div>
              {collateralForm.itemDescription && (
                <div className="confirm-field">
                  <strong>{texts.step4?.itemDescription || 'Item Description'}:</strong> {collateralForm.itemDescription}
                </div>
              )}
            </div>

            {/* Loan Terms Summary */}
            <div style={{ marginBottom: '2rem' }}>
              <h4 className="confirm-section-title">
                {texts.step4?.loanTerms || 'Loan Terms'}
              </h4>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="confirm-field">
                  <strong>{texts.step4?.disbursedAmount || 'Disbursed Amount'}:</strong> ₹{parseFloat(loanTerms.disbursedAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.interestRate || 'Interest Rate'}:</strong> {loanTerms.interestRate}% {texts.step4?.perMonth || 'per month'}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.tenure || 'Tenure'}:</strong> {loanTerms.tenure} {texts.step4?.months || 'months'}
                </div>
                <div className="confirm-field">
                  <strong>{texts.step4?.ltvRate || 'LTV Rate'}:</strong> ₹{loanTerms.lendingRate} {texts.step4?.perGram || 'per gram'}
                </div>
              </div>
              
              {/* Total Calculation */}
              {loanTerms.disbursedAmount && loanTerms.interestRate && loanTerms.tenure && (
                <div className="calc-preview-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="calc-preview-label">Principal:</span>
                    <span className="calc-preview-value" style={{ fontSize: '1.125rem' }}>₹{parseFloat(loanTerms.disbursedAmount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: createdLoanSummary ? 'flex-end' : 'space-between', alignItems: 'flex-start' }}>
            {!createdLoanSummary && (
              <div>
                <h2 className="card-title">{texts.title || 'Create a New Loan'}</h2>
                <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  {texts.subtitle || 'Follow the steps to originate a new loan.'}
                </p>
              </div>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0.5rem',
                borderRadius: '4px',
                color: '#6b7280',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f3f4f6';
                e.target.style.color = '#ef4444';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#6b7280';
              }}
              title="Close and go to Dashboard"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {createdLoanSummary ? (
          <div style={{ padding: '3rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem' }}>
            <div className="info-banner-success" style={{
              textAlign: 'center',
              width: '100%',
              maxWidth: '640px'
            }}>
              Successfully created loan for <strong>{createdLoanSummary.borrower.fullName}</strong>.
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                className="btn"
                onClick={printBill}
                style={{
                  minWidth: '240px',
                  backgroundColor: '#111827',
                  color: 'white',
                  borderColor: '#111827',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                Generate Bill (PDF)
              </button>
              <button
                className="btn btn-outline"
                onClick={resetCreateLoan}
                style={{ minWidth: '200px' }}
              >
                Create Another Loan
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Step Navigation */}
            <div className="tabs" style={{ marginBottom: '2rem' }}>
              {steps.map(step => (
                <button
                  key={step.id}
                  className={`tab ${currentStep === step.id ? 'active' : ''}`}
                  onClick={() => setCurrentStep(step.id)}
                >
                  {step.label}
                </button>
              ))}
            </div>

            {/* Step Content */}
            <div style={{ marginBottom: '2rem' }}>
              {renderStepContent()}
            </div>

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button 
                className="btn btn-outline"
                onClick={handleBack}
                disabled={currentStep === 1}
              >
                <ArrowLeft size={16} />
                {texts.buttons?.back || 'Back'}
              </button>
              
              {currentStep === steps.length ? (
                <button 
                  className="btn btn-success"
                  onClick={handleSubmitLoan}
                  disabled={isSubmitting || !selectedBorrower}
                >
                  <CheckCircle size={16} />
                  {isSubmitting ? (texts.buttons?.creating || 'Creating Loan...') : (texts.buttons?.confirmCreateLoan || 'Confirm & Create Loan')}
                </button>
              ) : (
              <button 
                className="btn btn-primary"
                onClick={handleNext}
              >
                {texts.buttons?.nextStep || 'Next Step'}
                <ArrowRight size={16} />
              </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Toast Notification */}
      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}

      
    </div>
  );
};

export default CreateLoan;
